import { TFunction } from 'react-i18next';
import { MultiChoiceOption } from '../../../../questionnaireTemplate.types';
import { OperatorConfig, OPERATOR_CONFIGS } from './questionValidationRules.config';
import { VALIDATION_CONSTRAINTS } from './questionValidationRules.constraints';
import { OptionSelectionCount, ValidationOperator, ValidationRule } from './questionValidationRules.types';

// ---------------------------------------------------------------------------
// Operator helpers
// ---------------------------------------------------------------------------

export const getAvailableOperators = (
  selectionCount: OptionSelectionCount,
  t: TFunction<'questionnaireTemplate'>,
): OperatorConfig[] => OPERATOR_CONFIGS(t).filter((config) => !config.availableWhen || config.availableWhen(selectionCount));

export const getOperatorConfig = (
  operator: ValidationOperator,
  t: TFunction<'questionnaireTemplate'>,
): OperatorConfig | undefined => OPERATOR_CONFIGS(t).find((config) => config.value === operator);

export const operatorRequiresMinCount = (operator: ValidationOperator, t: TFunction<'questionnaireTemplate'>): boolean =>
  getOperatorConfig(operator, t)?.requiresMinCount ?? false;

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------

/** Converts a zero-based index to an uppercase letter label: 0 → "A", 1 → "B", etc. */
export const getOptionLabel = (index: number): string => String.fromCharCode(65 + index);

/** Builds the initial local ValidationRule from persisted store values. */
export const getInitialValidationRule = (
  options: MultiChoiceOption[] | undefined,
  operator: ValidationOperator | undefined,
  selectionCount: OptionSelectionCount | undefined,
  minCount: number | undefined,
): ValidationRule => {
  const selectedOptionIds = (options ?? []).filter((option) => option.isPreferredAnswer).map((option) => option.id);

  return {
    selectionCount: selectionCount ?? VALIDATION_CONSTRAINTS.minSelectedOptions,
    operator: operator ?? ValidationOperator.IS_EQUAL,
    selectedOptionIds,
    minCount: operator === ValidationOperator.AT_LEAST ? minCount ?? VALIDATION_CONSTRAINTS.AT_LEAST.minCountMin : undefined,
  };
};

/** Toggles an option in/out of the selected list, respecting the selection limit. */
export const toggleOptionSelection = (
  currentSelectedIds: string[],
  optionId: string,
  selectionLimit: OptionSelectionCount,
): string[] => {
  const isSelected = currentSelectedIds.includes(optionId);

  if (isSelected) return currentSelectedIds.filter((id) => id !== optionId);
  if (selectionLimit === 'any') return [...currentSelectedIds, optionId];
  if (currentSelectedIds.length < selectionLimit) return [...currentSelectedIds, optionId];

  return selectionLimit === 1 ? [optionId] : [...currentSelectedIds.slice(1), optionId];
};

/** Trims the selected IDs list to fit a new (potentially smaller) count. */
export const adjustSelectedOptionsForCount = (selectedIds: string[], newCount: OptionSelectionCount): string[] =>
  newCount === 'any' ? selectedIds : selectedIds.slice(0, newCount);

/** Returns true when the current operator is not valid for the given selection count. */
export const shouldChangeOperator = (
  currentOperator: ValidationOperator,
  newSelectionCount: OptionSelectionCount,
  t: TFunction<'questionnaireTemplate'>,
): boolean => !getAvailableOperators(newSelectionCount, t).some((op) => op.value === currentOperator);

// ---------------------------------------------------------------------------
// Per-operator validators
// ---------------------------------------------------------------------------

/**
 * IS_EQUAL — author-selected count must equal exactly what the candidate picks.
 *   [n]   → selectedCount === n
 *   [any] → selectedCount ≤ totalOptions
 */
const isValidForIsEqual = (selectedCount: number, selectionCount: OptionSelectionCount, totalOptions: number): boolean => {
  /* istanbul ignore next */
  if (selectedCount < VALIDATION_CONSTRAINTS.minSelectedOptions) return false;
  const max = VALIDATION_CONSTRAINTS.IS_EQUAL.maxAllowed(selectionCount, totalOptions);
  return selectionCount === 'any' ? selectedCount <= max : selectedCount === selectionCount;
};

/**
 * HAS_ANSWER (Must Include) — author marks 1…n options the candidate must pick.
 *   [n]   → selectedCount ≤ n
 *   [any] → selectedCount ≤ totalOptions
 */
const isValidForHasAnswer = (selectedCount: number, selectionCount: OptionSelectionCount, totalOptions: number): boolean => {
  /* istanbul ignore next */
  if (selectedCount < VALIDATION_CONSTRAINTS.minSelectedOptions) return false;
  const max = VALIDATION_CONSTRAINTS.HAS_ANSWER.maxAllowed(selectionCount, totalOptions);
  return selectedCount <= max;
};

/**
 * DOES_NOT_HAVE_ANSWER (Must Not Include) — at least one option must stay unblocked.
 *   [n]   → selectedCount ≤ totalOptions − n
 *   [any] → selectedCount ≤ totalOptions − 1
 */
const isValidForDoesNotHaveAnswer = (
  selectedCount: number,
  selectionCount: OptionSelectionCount,
  totalOptions: number,
): boolean => {
  /* istanbul ignore next */
  if (selectedCount < VALIDATION_CONSTRAINTS.minSelectedOptions) return false;
  const max = VALIDATION_CONSTRAINTS.DOES_NOT_HAVE_ANSWER.maxAllowed(selectionCount, totalOptions);
  return selectedCount <= max;
};

/**
 * AT_LEAST (Must Include At Least) — candidate must pick at least minCount of the chosen options.
 *   selectedCount must be in range [minCount … totalOptions]
 */
const isValidForAtLeast = (selectedCount: number, minCount: number, totalOptions: number): boolean =>
  selectedCount >= minCount && selectedCount <= totalOptions;

// ---------------------------------------------------------------------------
// Validation error messages
// ---------------------------------------------------------------------------

export const noOptionsSelectedError = (t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.noOptionSelected');

// ── IS_EQUAL ─────────────────────────────────────────────────────────────────

export const isEqualExactCountError = (count: number, t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.isEqual.exactCountRequired', { count });

export const isEqualExceedsAvailableError = (t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.isEqual.exceedsAvailable');

// ── HAS_ANSWER (Must Include) ─────────────────────────────────────────────────

export const mustIncludeTooManyError = (count: number, t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.mustInclude.tooManySelected', { count });

export const mustIncludeExceedsAvailableError = (t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.mustInclude.exceedsAvailable');

// ── DOES_NOT_HAVE_ANSWER (Must Not Include) ───────────────────────────────────

export const mustNotIncludeNeedsMoreOptionsError = (t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.mustNotInclude.needsMoreOptions');

export const mustNotIncludeMustLeaveOneOptionError = (t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.mustNotInclude.mustLeaveOneOption');

export const mustNotIncludeTooManyError = (max: number, t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.mustNotInclude.tooManySelected', { max, count: max });

// ── AT_LEAST (Must Include At Least) ──────────────────────────────────────────

export const atLeastMinCountRequiredError = (t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.atLeast.minCountRequired');

export const atLeastMinExceedsSelectionError = (
  minCount: number,
  selectionCount: OptionSelectionCount,
  t: TFunction<'questionnaireTemplate'>,
): string =>
  t('formBuilder.validationErrors.validationRules.atLeast.minExceedsSelection', {
    minCount,
    selectionCount: selectionCount === 'any' ? t('formBuilder.canvas.element.validationRules.any') : selectionCount,
    count: minCount,
  });

export const atLeastNotEnoughOptionsError = (minCount: number, t: TFunction<'questionnaireTemplate'>): string =>
  t('formBuilder.validationErrors.validationRules.atLeast.notEnoughOptionsSelected', { minCount, count: minCount });

// ---------------------------------------------------------------------------
// Main validation entry point
//
// Returns the first relevant error string, or null when the rule is valid.
// ---------------------------------------------------------------------------

export const getValidationErrorMessage = (
  rule: Readonly<{
    selectionCount: OptionSelectionCount;
    operator: ValidationOperator;
    minCount?: number;
    selectedOptionIds: string[];
  }>,
  totalOptions: number,
  t: TFunction<'questionnaireTemplate'>,
): string | null => {
  const selectionCount: OptionSelectionCount =
    rule.selectionCount === 'any' ? 'any' : parseInt(rule.selectionCount?.toString(), 10);

  const selectedCount = rule.selectedOptionIds.length;

  if (selectedCount === 0) return noOptionsSelectedError(t);

  switch (rule.operator) {
    case ValidationOperator.IS_EQUAL: {
      if (isValidForIsEqual(selectedCount, selectionCount, totalOptions)) break;
      if (selectionCount === 'any') return isEqualExceedsAvailableError(t);
      return isEqualExactCountError(selectionCount, t);
    }

    case ValidationOperator.HAS_ANSWER: {
      if (isValidForHasAnswer(selectedCount, selectionCount, totalOptions)) break;
      const max = VALIDATION_CONSTRAINTS.HAS_ANSWER.maxAllowed(selectionCount, totalOptions);
      if (selectionCount === 'any') return mustIncludeExceedsAvailableError(t);
      return mustIncludeTooManyError(max, t);
    }

    case ValidationOperator.DOES_NOT_HAVE_ANSWER: {
      if (totalOptions <= 1) return mustNotIncludeNeedsMoreOptionsError(t);
      if (selectionCount !== 'any' && selectionCount >= totalOptions) return mustNotIncludeMustLeaveOneOptionError(t);
      if (isValidForDoesNotHaveAnswer(selectedCount, selectionCount, totalOptions)) break;
      const max = VALIDATION_CONSTRAINTS.DOES_NOT_HAVE_ANSWER.maxAllowed(selectionCount, totalOptions);
      return mustNotIncludeTooManyError(max, t);
    }

    case ValidationOperator.AT_LEAST: {
      if (!rule.minCount) return atLeastMinCountRequiredError(t);
      const maxAllowedMin = VALIDATION_CONSTRAINTS.AT_LEAST.minCountMax(selectionCount, totalOptions);
      if (rule.minCount > maxAllowedMin) return atLeastMinExceedsSelectionError(rule.minCount, selectionCount, t);
      if (isValidForAtLeast(selectedCount, rule.minCount, totalOptions)) break;
      return atLeastNotEnoughOptionsError(rule.minCount, t);
    }
    default:
      return null;
  }

  return null;
};
